import React, { useCallback, useContext, useState } from 'react';
import { Col, Tag } from 'antd';
import {
    CalendarOutlined,
    ClockCircleOutlined,
    GiftOutlined,
    TagOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { IAppUser, IReward, IVoucher } from '../../interfaces';
import { formatCpf } from '../../utils/Masks';
import { formatDateStringToDateAndTime } from '../../utils/Conversores';
import { ThemeContext } from '../../contexts/ThemeContext';

interface IProps {
    voucher?: IVoucher;
    reward?: IReward;
    user?: IAppUser;
    onClick?: (voucher: IVoucher) => void;
    clickable?: boolean;
    fullWidth?: boolean;
}

export default function Voucher({
    voucher,
    reward,
    user,
    onClick = () => { },
    clickable = true,
    fullWidth = false,
}: IProps) {
    const [isMouseIn, setIsMouseIn] = useState<boolean>(false);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const handleMouseEnter = useCallback(() => {
        if (!clickable) return;
        setIsMouseIn(true);
    }, [clickable]);

    const handleMouseLeave = useCallback(() => {
        if (!clickable) return;
        setIsMouseIn(false);
    }, [clickable]);

    if (!voucher || !reward || !user) {
        return null;
    }

    const isDelivered = voucher.status === 'Resgatado';
    const isExpired = voucher.status === 'Expirado';

    const accentBg = isDelivered
        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
        : isExpired
        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
        : 'linear-gradient(135deg, #1677ff 0%, #0958d9 100%)';

    return (
        <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 24 }}
            xl={{ span: fullWidth ? 24 : 12 }}
            xxl={{ span: fullWidth ? 24 : 12 }}
        >
            <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={() => onClick(voucher)}
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    minHeight: 160,
                    backgroundColor: isDark ? '#191919' : '#ffffff',
                    borderRadius: 14,
                    overflow: 'hidden',
                    border: `1px solid ${isDark ? '#2d2d2d' : '#eaedf1'}`,
                    boxShadow: isMouseIn
                        ? isDark
                            ? '0 8px 24px rgba(0, 0, 0, 0.5)'
                            : '0 8px 24px rgba(22, 119, 255, 0.12)'
                        : isDark
                        ? '0 4px 14px rgba(0, 0, 0, 0.35)'
                        : '0 2px 10px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.25s ease-in-out',
                    transform: isMouseIn && clickable ? 'translateY(-2px)' : 'none',
                    cursor: clickable ? 'pointer' : 'default',
                    position: 'relative',
                }}
            >
                {/* Left Section: Details */}
                <div
                    style={{
                        flex: '1 1 70%',
                        padding: '16px 20px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: 10,
                    }}
                >
                    {/* Header: Reward Name & Points */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                            <div
                                style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 8,
                                    background: isDark ? 'rgba(22, 119, 255, 0.15)' : '#e6f4ff',
                                    color: '#1677ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 18,
                                    flexShrink: 0,
                                }}
                            >
                                <GiftOutlined />
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                    Recompensa
                                </div>
                                <div
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 700,
                                        color: isDark ? '#f1f5f9' : '#1e293b',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                    title={reward.name}
                                >
                                    {reward.name}
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                background: isDark ? 'rgba(16, 185, 129, 0.15)' : '#ecfdf5',
                                border: '1px solid #10b981',
                                color: '#10b981',
                                padding: '3px 10px',
                                borderRadius: 16,
                                fontWeight: 700,
                                fontSize: 13,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                            }}
                        >
                            <TagOutlined style={{ fontSize: 12 }} />
                            {(voucher.points_used || 0).toLocaleString('pt-BR')} P
                        </div>
                    </div>

                    {/* Middle: User info */}
                    <div
                        style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            background: isDark ? '#141414' : '#f8fafc',
                            border: `1px solid ${isDark ? '#262626' : '#f1f5f9'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 8,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            <UserOutlined style={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#e2e8f0' : '#334155' }}>
                                {user.name}
                            </span>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 500, color: isDark ? '#94a3b8' : '#64748b', fontFamily: 'monospace' }}>
                            CPF: {formatCpf(user.cpf)}
                        </span>
                    </div>

                    {/* Bottom: Dates */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: 11.5,
                            color: isDark ? '#94a3b8' : '#64748b',
                            flexWrap: 'wrap',
                            gap: 8,
                        }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <ClockCircleOutlined /> Resgate:{' '}
                            <strong style={{ color: isDark ? '#cbd5e1' : '#475569' }}>
                                {formatDateStringToDateAndTime(voucher.created_at)}
                            </strong>
                        </span>

                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <CalendarOutlined /> Expira:{' '}
                            <strong style={{ color: isExpired ? '#ef4444' : isDark ? '#cbd5e1' : '#475569' }}>
                                {formatDateStringToDateAndTime(voucher.validity)}
                            </strong>
                        </span>
                    </div>
                </div>

                {/* Perforated separator notch / line */}
                <div
                    style={{
                        width: 1,
                        borderLeft: `2px dashed ${isDark ? '#333333' : '#cbd5e1'}`,
                        position: 'relative',
                        margin: '10px 0',
                    }}
                />

                {/* Right Section: Code & Status */}
                <div
                    style={{
                        flex: '0 0 160px',
                        background: accentBg,
                        color: '#ffffff',
                        padding: 16,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 8,
                    }}
                >
                    <div
                        style={{
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: 1,
                            textTransform: 'uppercase',
                            opacity: 0.9,
                        }}
                    >
                        Código de Resgate
                    </div>

                    <div
                        style={{
                            fontFamily: 'monospace',
                            fontSize: 22,
                            fontWeight: 800,
                            letterSpacing: 2,
                            padding: '4px 10px',
                            background: 'rgba(0, 0, 0, 0.2)',
                            borderRadius: 6,
                            color: '#ffffff',
                            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)',
                        }}
                    >
                        {voucher.redemption_code}
                    </div>

                    <Tag
                        style={{
                            margin: 0,
                            borderRadius: 12,
                            fontWeight: 600,
                            border: 'none',
                            background: '#ffffff',
                            color: isDelivered ? '#059669' : isExpired ? '#dc2626' : '#0958d9',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                        }}
                    >
                        {voucher.status}
                    </Tag>
                </div>
            </div>
        </Col>
    );
}